'use client';

import CommunityIcon from '@mui/icons-material/PhotoFilter';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Tab, Tabs } from '@mui/material';
import {
  CreateGameButton,
  MyGamesTabContent,
} from '@wt/app/community/_components/MyGamesTabContent';
import PlayAgainModal from '@wt/game/modals/PlayAgainModal';
import { useBannerAdOpen } from '@wt/game/providers/bannerAdOpen/BannerAdOpenProvider';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';
import { BackButton } from '@wt/shared/components/BackButton';
import useAnimatedRouter from '@wt/shared/hooks/useAnimatedRouter';
import { isNil } from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { clearCommunityGame, loadCommunityCompleted } from '../utils';
import { CommunityGamesTabContent } from './CommunityGamesTabContent';

export const GAMES_PER_LOAD = 20;

export const CommunityContent = () => {
  const router = useRouter();
  const { animatedRoute } = useAnimatedRouter();
  const { loggedIn } = useUserProvider();
  const { adOpen } = useBannerAdOpen();
  const searchParams = useSearchParams();
  const tabFilter = searchParams.get('tab');
  const [tab, setTab] = useState<number>(tabFilter === 'mine' ? 1 : 0);
  const [sortBy, setSortBy] = useState<string>('newest');
  const [sortOrder, setSortOrder] = useState<string>('descending');
  const [daysPeriod, setDaysPeriod] = useState<number>(999999);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [showPlayAgainModal, setShowPlayAgainModal] = useState<boolean>(false);

  useEffect(() => {
    if (!loggedIn && tab === 1) {
      localStorage.setItem('post-login-location', window.location.pathname);
      router.push('/login');
    }
  }, [loggedIn, tab]);

  const top = useMemo(() => {
    return adOpen
      ? 'top-[103px] md:top-[112px] lg:top-[164px]'
      : 'top-[57px] md:top-[64px] lg:top-[77px]';
  }, [adOpen]);

  const navToCommunityGame = ({
    slug,
    clearGuesses,
    viewResults,
  }: {
    slug: string;
    clearGuesses: boolean;
    viewResults: boolean;
  }): void => {
    if (clearGuesses) {
      clearCommunityGame(slug, loggedIn);
    }
    animatedRoute(`/community/game/${slug}${viewResults ? '/summary' : ''}`);
    return;
  };

  useEffect(() => {
    if (
      !isNil(selectedGame) &&
      loadCommunityCompleted().includes(selectedGame)
    ) {
      setShowPlayAgainModal(true);
    } else if (!isNil(selectedGame)) {
      navToCommunityGame({
        slug: selectedGame,
        clearGuesses: false,
        viewResults: false,
      });
    }
  }, [selectedGame]);

  useEffect(() => {
    if (!showPlayAgainModal) {
      setSelectedGame(null);
    }
  }, [showPlayAgainModal]);

  return (
    <>
      <div className="mb-5 flex w-full flex-col items-center justify-center px-4">
        <div className="flex w-full flex-col items-center md:max-w-screen-xl">
          <div
            className={`sticky py-3 ${top} z-10 flex w-full flex-col items-center gap-3 bg-base-100 md:py-5`}
          >
            <div className="relative mb-2 flex w-full items-center justify-center">
              <BackButton />
              <div className="w-full items-center justify-center text-center text-xl md:text-2xl">
                <span className="text-highlight dark:text-highlight-dark">
                  <CommunityIcon className="pr-1" />
                </span>
                <span>Community</span>
              </div>
              <CreateGameButton />
            </div>
            <div className="flex w-full flex-col items-center gap-3 md:flex-row md:justify-between">
              <div className="flex w-full justify-center md:justify-start">
                <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                  <Tab label="All Games" style={getTabStyle(tab === 0)} />
                  <Tab label="My Games" style={getTabStyle(tab === 1)} />
                </Tabs>
              </div>
              {tab === 0 && (
                <div className="flex w-full flex-row gap-2 items-center justify-center md:justify-end">
                  <SortOptions
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    daysPeriod={daysPeriod}
                    setDaysPeriod={setDaysPeriod}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                  />
                </div>
              )}
            </div>
          </div>
          {tab === 0 ? (
            <CommunityGamesTabContent
              sortBy={sortBy}
              daysPeriod={daysPeriod}
              sortOrder={sortOrder}
              handleClickGame={setSelectedGame}
            />
          ) : (
            <MyGamesTabContent handleClickGame={setSelectedGame} />
          )}
        </div>
      </div>
      {showPlayAgainModal && !isNil(selectedGame) && (
        <PlayAgainModal
          closeModal={() => setShowPlayAgainModal(false)}
          handleClickSeeResults={() =>
            navToCommunityGame({
              slug: selectedGame,
              clearGuesses: false,
              viewResults: true,
            })
          }
          handleClickPlayAgain={() =>
            navToCommunityGame({
              slug: selectedGame,
              clearGuesses: true,
              viewResults: false,
            })
          }
        />
      )}
    </>
  );
};

const getTabStyle = (selected: boolean) => {
  return {
    paddingLeft: '16px',
    paddingRight: '16px',
    fontSize: '18px',
    fontWeight: selected ? 'bold' : 'normal',
  };
};

const SortOptions = ({
  sortBy,
  setSortBy,
  daysPeriod,
  setDaysPeriod,
  sortOrder,
  setSortOrder,
}: {
  sortBy: string;
  setSortBy: (e: string) => void;
  daysPeriod: number;
  setDaysPeriod: (e: number) => void;
  sortOrder: string;
  setSortOrder: (e: string) => void;
}) => {
  return (
    <>
      <FormControl>
        <InputLabel id="community-content__period-select-label">
          Time
        </InputLabel>
        <Select
          labelId="community-content__period-select-label"
          value={daysPeriod}
          label="Time"
          onChange={(e) => {
            setDaysPeriod(+e.target.value);
          }}
        >
          <MenuItem value={'30'}>Past Month</MenuItem>
          <MenuItem value={'183'}>Past 6 Months</MenuItem>
          <MenuItem value={'365'}>Past Year</MenuItem>
          <MenuItem value={'999999'}>All Time</MenuItem>
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel id="community-content__sort-by-select-label">
          Sort by
        </InputLabel>
        <Select
          labelId="community-content__sort-by-select-label"
          value={sortBy}
          label="Sort by"
          onChange={(e) => {
            setSortBy(e.target.value);
          }}
        >
          <MenuItem value={'newest'}>Date Created</MenuItem>
          <MenuItem value={'likesScore'}>Rating</MenuItem>
          <MenuItem value={'timesPlayedScore'}>Number of Plays</MenuItem>
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel id="community-content__sort-order-select-label">
          Order
        </InputLabel>
        <Select
          labelId="community-content__sort-order-select-label"
          value={sortOrder}
          label="Order"
          onChange={(e) => {
            setSortOrder(e.target.value);
          }}
        >
          <MenuItem value={'ascending'}>Asc</MenuItem>
          <MenuItem value={'descending'}>Desc</MenuItem>
        </Select>
      </FormControl>
    </>
  );
};
